/**
 * @typedef {object} AudioUrls
 * @property {string} correct - URL to the sound effect for correct answers
 * @property {string} incorrect - URL to the sound effect for incorrect answers
 * @property {string} streak - URL to the sound effect for answer streaks
 * @property {string} celebration - URL to the sound effect for celebrations and milestones
 */

/**
 * Extend Window interface to include quiz audio URLs
 * @typedef {Window & { quizAudioUrls?: AudioUrls }} WindowWithQuizAudio
 */

/**
 * @typedef {object} QuizResponse
 * @property {OpenEndedQuizAnswer|MultipleChoiceQuizAnswer} result undocumented
 * @property {"multipleChoiceQuestion"|"openEndedQuestion"} quizType undocumented
 * @property {boolean} isLessonItemCompleted undocumented
 * @property {boolean} isLessonCompleted undocumented
 * @property {Link} links undocumented
 */

/**
 * @typedef {object} OpenEndedQuizAnswer
 * @property {string} acceptedText undocumented
 * @property {number} potentialPointsAdded undocumented
 */

/**
 * @typedef {object} MultipleChoiceQuizAnswer
 * @property {number} correctChoice undocumented
 * @property {number} pointsAdded undocumented
 * @property {number} earnedScore Enrollment score after this answer
 * @property {string|null} instructorExplanationHtml undocumented
 */

/**
 * @typedef {object} Link
 * @property {string|null} update undocumented
 */

/**
 * @typedef {object} CourseAnswerSubmittedEventDetails
 * @property {object} result undocumented
 * @property {number} result.pointsAdded undocumented
 * @property {number} result.potentialPointsAdded undocumented
 * @property {number|null} result.earnedScore Enrollment score after the answer; null for OEQ, which earns no points on submit
 * @property {boolean} isLessonItemCompleted undocumented
 * @property {boolean} isLessonCompleted undocumented
 * @property {number} lessonId undocumented
 * @property {number} lessonItemId undocumented
 * @property {HTMLElement|null} quizResultElement null for OEQ
 */

import {Button} from '../../modules/button.js';
import {Notification} from '../../modules/notification.js';
import {requestDialog} from '../../modules/dialog.js';
import {countWords} from '../../utils/markupHelpers.js';
import {buildElement} from '../../modules/htmlElementBuilder.js';
import {getHttpClient} from '../../modules/httpClient.js';
import {triggerPointsAnimation} from '../../modules/flyingPointsAnimation.js';
import {Spinner} from '../../modules/spinner.js';
import {t} from '../../localization/translate.js';

/** @type {boolean|undefined} */
let hasScorePointsSection;

// ============================================
// Sound Effects
// ============================================
const SOUND_ENABLED_KEY = 'ixdf-quiz-sound-enabled';

/**
 * Audio file URLs - uses S3 URLs passed from backend via window.quizAudioUrls
 * @type {{[key: string]: string}|null}
 */
// @ts-ignore - quizAudioUrls is set in Blade template
const SOUND_FILES = window.quizAudioUrls || null;

/** @type {Map<string, HTMLAudioElement>} */
const audioCache = new Map();

/**
 * Get or create an Audio element for the given sound
 * @param {string} soundKey - Key from SOUND_FILES
 * @returns {HTMLAudioElement|null}
 */
function getAudioElement(soundKey) {
    // Gracefully handle missing sound files configuration
    if (!SOUND_FILES) {
        return null;
    }

    const path = SOUND_FILES[soundKey];
    if (!path) {
        return null;
    }

    // Return cached audio element if exists
    if (audioCache.has(soundKey)) {
        return audioCache.get(soundKey) || null;
    }

    // Create and cache new audio element
    const audio = new Audio(path);
    audio.preload = 'auto';
    audio.volume = 0.5;
    audioCache.set(soundKey, audio);
    return audio;
}

/**
 * Play a sound by key
 * @param {string} soundKey - Key from SOUND_FILES
 */
function playSound(soundKey) {
    if (!isSoundEnabled()) {
        return;
    }

    const audio = getAudioElement(soundKey);
    if (!audio) {
        return;
    }

    // Reset to start if already playing
    audio.currentTime = 0;
    audio.play().catch(() => {
        // Ignore play errors (e.g., user hasn't interacted yet)
    });
}

/**
 * Check if sound effects are enabled
 * @returns {boolean}
 */
function isSoundEnabled() {
    try {
        const stored = localStorage.getItem(SOUND_ENABLED_KEY);
        // Default to enabled if not set
        return stored === null ? true : stored === 'true';
    } catch {
        return true;
    }
}

/**
 * Set sound effects preference
 * @param {boolean} enabled
 */
function setSoundEnabled(enabled) {
    try {
        localStorage.setItem(SOUND_ENABLED_KEY, String(enabled));
    } catch {
        // Ignore storage errors
    }
}

/**
 * Toggle sound effects on/off
 * @returns {boolean} New state
 */
// eslint-disable-next-line no-unused-vars -- Public API for sound toggle UI (future feature)
function toggleSound() {
    const newState = !isSoundEnabled();
    setSoundEnabled(newState);
    return newState;
}

/**
 * Play a "correct" sound
 */
function playCorrectSound() {
    playSound('correct');
}

/**
 * Play an "incorrect" sound
 */
function playIncorrectSound() {
    playSound('incorrect');
}

/**
 * Play a celebration/milestone sound
 */
// eslint-disable-next-line no-unused-vars -- Available for milestone celebrations
function playCelebrationSound() {
    playSound('celebration');
}

/**
 * Play a streak achievement sound
 */
function playStreakSound() {
    playSound('streak');
}

/**
 * Preload all audio files asynchronously
 * Called on page load to ensure instant playback when needed
 */
function preloadSounds() {
    // Gracefully handle missing sound files configuration
    if (!SOUND_FILES) {
        return;
    }

    // Schedule preload during idle time to avoid blocking main thread
    setTimeout(() => {
        for (const soundKey of Object.keys(SOUND_FILES)) {
            getAudioElement(soundKey);
        }
    }, 100);
}

// ============================================
// Quiz Streak Module
// ============================================
const STREAK_STORAGE_KEY = 'ixdf-quiz-streak';
const STREAK_EXPIRY_KEY = 'ixdf-quiz-streak-expiry';
const STREAK_EXPIRY_MINUTES = 30; // Streak expires after 30 minutes of inactivity

/**
 * Get current streak from sessionStorage
 * @returns {number}
 */
function getStreak() {
    try {
        const expiry = sessionStorage.getItem(STREAK_EXPIRY_KEY);
        if (expiry && Date.now() > parseInt(expiry, 10)) {
            // Streak expired, reset it
            sessionStorage.removeItem(STREAK_STORAGE_KEY);
            sessionStorage.removeItem(STREAK_EXPIRY_KEY);
            return 0;
        }
        return parseInt(sessionStorage.getItem(STREAK_STORAGE_KEY) || '0', 10);
    } catch {
        return 0;
    }
}

/**
 * Set streak in sessionStorage
 * @param {number} streak
 */
function setStreak(streak) {
    try {
        sessionStorage.setItem(STREAK_STORAGE_KEY, String(streak));
        // Update expiry time
        const expiryTime = Date.now() + (STREAK_EXPIRY_MINUTES * 60 * 1000);
        sessionStorage.setItem(STREAK_EXPIRY_KEY, String(expiryTime));
    } catch {
        // Ignore storage errors
    }
}

/**
 * Trigger haptic feedback on supported devices
 * @param {number} duration - Vibration duration in milliseconds
 */
function triggerHapticFeedback(duration = 100) {
    if (navigator.vibrate) {
        navigator.vibrate(duration);
    }
}

/**
 * Apply shake animation to an element for incorrect answers
 * @param {HTMLElement} element - The element to shake
 */
function shakeElement(element) {
    // Use CSS utility class for shake animation
    element.classList.add('course-quiz-shake');
    element.addEventListener('animationend', () => {
        element.classList.remove('course-quiz-shake');
    }, {once: true});

    // Haptic feedback for incorrect answer
    triggerHapticFeedback(50);
}

/**
 * Update and display streak indicator
 * @param {boolean} isCorrect - Whether the answer was correct
 */
function updateStreakIndicator(isCorrect) {
    let currentStreak = getStreak();

    if (isCorrect) {
        currentStreak += 1;
        setStreak(currentStreak);
    } else {
        currentStreak = 0;
        setStreak(0);
    }

    // Only show streak for 2+ correct answers in a row
    if (currentStreak >= 2) {
        showStreakBadge(currentStreak);
    }
}

/**
 * Show streak badge with animation
 * @param {number} streak - Current streak count
 */
function showStreakBadge(streak) {
    // Remove any existing streak badge
    const existingBadge = document.querySelector('[data-streak-badge]');
    if (existingBadge) {
        existingBadge.remove();
    }

    const message = streak >= 5
        ? t("🔥🔥 :count in a row! You're on fire!", {count: streak})
        : t('🔥 :count in a row!', {count: streak});

    const badge = document.createElement('div');
    badge.className = 'course-streak-badge';
    badge.setAttribute('data-streak-badge', '');
    badge.setAttribute('aria-live', 'polite');
    badge.textContent = message;

    document.body.append(badge);

    // Play streak sound
    playStreakSound();

    // Animate in (with slight delay for DOM to settle)
    setTimeout(() => {
        badge.setAttribute('data-visible', '');
    }, 50);

    // Animate out after delay
    setTimeout(() => {
        badge.removeAttribute('data-visible');
        setTimeout(() => badge.remove(), 300);
    }, 3000);

    triggerHapticFeedback(streak >= 5 ? 150 : 100);
}

// ============================================
// Daily Learning Streak Module
// ============================================
const DAILY_STREAK_KEY = 'ixdf-daily-learning-streak';
const DAILY_STREAK_DATE_KEY = 'ixdf-daily-learning-last-date';

/**
 * Get the current date as YYYY-MM-DD string
 * @returns {string}
 */
function getTodayDateString() {
    const today = new Date();
    return today.toISOString().split('T')[0];
}

/**
 * Get daily learning streak data
 * @returns {{ streak: number, lastDate: string|null }}
 */
function getDailyStreak() {
    try {
        const streak = parseInt(localStorage.getItem(DAILY_STREAK_KEY) || '0', 10);
        const lastDate = localStorage.getItem(DAILY_STREAK_DATE_KEY);
        return {streak, lastDate};
    } catch {
        return {streak: 0, lastDate: null};
    }
}

/**
 * Update daily learning streak based on activity
 * Call this when the user completes a learning activity (quiz answered, lesson viewed)
 */
function updateDailyLearningStreak() {
    try {
        const today = getTodayDateString();
        const {streak, lastDate} = getDailyStreak();

        // Already logged today
        if (lastDate === today) {
            return;
        }

        // Check if this is consecutive day
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayString = yesterday.toISOString().split('T')[0];

        let newStreak;
        if (lastDate === yesterdayString) {
            // Consecutive day - increment streak
            newStreak = streak + 1;
        } else if (lastDate === null) {
            // First time - start streak
            newStreak = 1;
        } else {
            // Streak broken - reset
            newStreak = 1;
        }

        localStorage.setItem(DAILY_STREAK_KEY, String(newStreak));
        localStorage.setItem(DAILY_STREAK_DATE_KEY, today);

        // Show daily streak badge for streaks >= 2
        if (newStreak >= 2) {
            showDailyStreakBadge(newStreak);
        }
    } catch {
        // Ignore storage errors
    }
}

/**
 * Show daily learning streak badge
 * @param {number} streak - Current daily streak count
 */
function showDailyStreakBadge(streak) {
    // Don't show if another streak badge is visible
    if (document.querySelector('[data-streak-badge]')) {
        return;
    }

    const message = t('📅 :count day learning streak!', {count: streak});

    const badge = document.createElement('div');
    badge.className = 'course-streak-badge';
    badge.setAttribute('data-streak-badge', '');
    badge.setAttribute('data-daily-streak', '');
    badge.setAttribute('aria-live', 'polite');
    badge.textContent = message;
    badge.style.background = 'linear-gradient(90deg, #059669, #10b981)'; // Green gradient for daily

    document.body.append(badge);

    // Animate in
    setTimeout(() => {
        badge.setAttribute('data-visible', '');
    }, 50);

    // Animate out after delay
    setTimeout(() => {
        badge.removeAttribute('data-visible');
        setTimeout(() => badge.remove(), 300);
    }, 4000);
}


/** @type {Array<{text: string, author: string}>} */
const QUOTES = [
    {text: t('“A person who never made a mistake never tried anything new.”'), author: t('Albert Einstein')},
    {text: t("“I've failed over and over and over again in my life. And that is why I succeed.”"), author: t('Michael Jordan')},
    {text: t('“It is hard to fail, but it is worse never to have tried to succeed.”'), author: t('Theodore Roosevelt')},
    {text: t("“I have not failed. I've just found 10,000 ways that won't work.”"), author: t('Thomas A. Edison')},
    {text: t('“Success depends on your willingness to fail and learn.”'), author: t('Thomas Edison')},
    {text: t('Anyone who has never made a mistake has never tried anything new.'), author: t('Albert Einstein')},
    {text: t('“Your best teacher is your last mistake.”'), author: t('Ralph Nader')},
    {text: t('Mistakes are, after all, the foundations of truth.'), author: t('Carl Jung')},
    {text: t('Failure is success if we learn from it.'), author: t('Malcolm Forbes')},
    {text: t('The only real mistake is the one from which we learn nothing.'), author: t('Henry Ford')},
    {text: t('Mistakes – call them unexpected learning experiences.'), author: t('Richard Bach')},
    {text: t("Don't waste a good mistake… learn from it."), author: t('Robert Kiyosaki')},
];

/**
 * Get random quote
 * @returns {HTMLElement}
 */
export function getRandomQuote() {
    const {text, author} = QUOTES[Math.floor(Math.random() * QUOTES.length)];

    const quoteBlockquote = buildElement('blockquote')
        .addClass('flex', 'justify-center', 'fg-title-lg', 'quoted-content', 'text-center')
        .getElement();
    quoteBlockquote.append(text, document.createElement('br'), `—${author}`);

    return quoteBlockquote;
}

/**
 * @returns {boolean}
 */
function hasScorePoints() {
    if (hasScorePointsSection !== undefined) {
        return hasScorePointsSection;
    }

    hasScorePointsSection = !!document.querySelector('[data-quiz-section][data-has-points]');
    return hasScorePointsSection;
}

/**
 * Get the Correct Answer Message
 * @param {number} pointsEarned
 * @returns {HTMLElement}
 */
function getCorrectAnswerMessage(pointsEarned) {
    const tickIcon = buildElement('div')
        .addClass('shrink-0', 'fg-text-status-success-default')
        .addHtml('<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" class="svg-icon fg-heading-sm"><use href="#icon-check"></use></svg>')
        .getElement();

    const messageText = buildElement('p')
        .addClass('quizResult__text', 'fg-body-lg')
        .getElement();
    const correctLabel = document.createElement('b');
    correctLabel.textContent = t('Correct!');
    messageText.append(correctLabel);
    if (hasScorePoints()) {
        messageText.append(' ', t('[0,1] +:count point towards your IxDF Course Certificate.|[2,*] +:count points towards your IxDF Course Certificate.', {}, pointsEarned));
    }

    const container = buildElement('div')
        .addClass('message', 'message--correct', 'flex', 'items-start', 'gap-8', 'fg-text-neutral-default')
        .getElement();

    container.append(tickIcon);
    container.append(messageText);

    // Add progress bar if points are enabled
    if (hasScorePoints()) {
        const progressBar = buildElement('div')
            .addClass('achievementProgressbar__part', 'quizResult__progressBar', 'js_quizResult__progressBar')
            .getElement();
        container.append(progressBar);
    }

    return container;
}

/**
 * Get Incorrect Answer Message
 * @returns {HTMLElement}
 */
function getIncorrectAnswerMessage() {
    const xIcon = buildElement('div')
        .addClass('shrink-0')
        .addHtml('<svg class="size-20 md:size-24 lg:size-26 xl:size-28 fill-current" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>')
        .getElement();

    const messageText = buildElement('p')
        .addClass('quizResult__text', 'fg-title-sm', 'font-bold')
        .addText(t('Incorrect!'))
        .getElement();

    const container = buildElement('div')
        .addClass('message', 'message--incorrect', 'flex', 'items-center', 'gap-8', 'fg-text-status-danger-default')
        .getElement();

    container.append(xIcon);
    container.append(messageText);
    return container;
}

/**
 * Show Multiple Choice Answer
 * @param {HTMLElement} form
 * @param {number} point
 * @param {number} correctAnswer
 * @param {number} answeredChoice
 */
function showMcqAnswer(form, point, correctAnswer, answeredChoice) {
    const isUserAnsweredCorrectly = correctAnswer === answeredChoice;

    // Get current streak before update to determine if this will trigger a streak badge
    const currentStreak = getStreak();
    const willShowStreak = isUserAnsweredCorrectly && (currentStreak + 1) >= 2;

    // Play sound effect based on answer correctness
    // Only play correct sound if NOT showing streak (avoid overlapping sounds)
    if (isUserAnsweredCorrectly) {
        if (!willShowStreak) {
            playCorrectSound();
        }
        // Streak sound is played in showStreakBadge when streak >= 2
    } else {
        playIncorrectSound();
    }

    // Update streak and show badge if applicable
    updateStreakIndicator(isUserAnsweredCorrectly);

    const resultMessage = isUserAnsweredCorrectly ? getCorrectAnswerMessage(point) : getIncorrectAnswerMessage();
    const quizResultContainer = form.querySelector('.js-quizResult');
    quizResultContainer.append(resultMessage);
    quizResultContainer.classList.remove('hidden');

    if (!isUserAnsweredCorrectly) {
        // Shake animation for incorrect answer
        shakeElement(form);
        /** @type {HTMLElement} */
        const assetsElement = form.querySelector('.js-quizAnswerDividerImage');
        const dividerImageSource = assetsElement ? assetsElement.dataset.dividerImage : undefined;
        const dividerImg = dividerImageSource ? buildElement('img').setAttribute('src', dividerImageSource).setAttribute('alt','').addClass('w-full', 'my-12', 'md:my-16', 'lg:my-20', 'xl:my-24').getElement() : buildElement('div').addClass('my-24').getElement();
        const quote = getRandomQuote();
        const answerResultAnimationDuration = 500;
        setTimeout(function () {
            quizResultContainer.append(dividerImg);
            quizResultContainer.append(quote);
        }, answerResultAnimationDuration + 300);
    }

    /** Replace correct answer radio button with green check icon if user got it right */
    const $correctChoice = form.querySelector(`input.js-answerChoice[value="${correctAnswer}"]`);
    const $correctLabel = $correctChoice.nextElementSibling;

    if (isUserAnsweredCorrectly) {
        // Replace radio button with green check icon for correct answer
        const checkIcon = buildElement('span')
            .addClass('shrink-0', 'flex', 'items-center', 'justify-center', 'fg-text-status-success-default')
            .addHtml('<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" class="svg-icon fg-heading-sm"><use href="#icon-check-circle"></use></svg>')
            .getElement();

        $correctChoice.replaceWith(checkIcon);
    }

    $correctLabel.classList.add('font-bold', 'fg-text-neutral-default');
    $correctLabel.append(' ', t('(Correct answer)'));

    form.querySelectorAll('.js-answerChoice').forEach((choiceInput) => {
        if (choiceInput.value !== String(correctAnswer)) {
            choiceInput.nextElementSibling?.classList.add('text-gray-500');
        }
    });
}

/**
 * Display result
 * @param {HTMLElement} form
 * @param {number} point
 * @param {number} correctAnswer
 * @param {string|null} instructorExplanationHtml
 */
function afterAnswerSubmitDisplayMcqResult(form, point, correctAnswer, instructorExplanationHtml) {
    /** @type {HTMLInputElement|null} */
    const $answerChoice = form.querySelector('input:checked');
    if (!$answerChoice) {
        // Form state was lost (e.g. restored/stale DOM): report and reload to show the
        // server-rendered result. The delay lets the report flush before navigation.
        window.dispatchEvent(new CustomEvent('log:error', {
            detail: new Error('MCQ result rendered without a checked answer choice'),
        }));
        setTimeout(() => window.location.reload(), 1000);
        return;
    }
    const answeredChoice = parseInt($answerChoice.value);

    showMcqAnswer(form, point, correctAnswer, answeredChoice);
    showInstructorExplanation(/** @type {HTMLFormElement} */ (form), instructorExplanationHtml);

    // Trigger flying points animation if answer is correct and points were awarded
    const isCorrect = correctAnswer === answeredChoice;
    if (isCorrect && point > 0) {
        const resultMessageText = form.querySelector('.quizResult__text');
        if (resultMessageText) {
            triggerPointsAnimation(point, resultMessageText);
        }
    }
}

/**
 * Display result
 * @param {HTMLElement} form
 * @param {string} acceptedText
 */
function afterAnswerSubmitDisplayOeqResult(form, acceptedText) {
    // hide RTE and display submitted html in a readonly div
    /** @type {HTMLElement|null} */
    const rtEditor = form.querySelector('rt-editor');
    rtEditor?.classList.add('hidden');
    // Show user's answer at the pseudo-disabled input
    form.querySelector('.js-quizAnswered').innerHTML = acceptedText;
    form.querySelector('.js-quizAnswered').classList.remove('hidden');
    // Show await-grading message
    form.querySelector('.js-quizAwaitGradingMessage').classList.remove('hidden');
    form.querySelector('.js-quizEdit')?.classList.remove('hidden');
}

/**
 * Display result
 * @param {HTMLFormElement} form
 * @param {Link} links
 */
function afterAnswerSubmitUpdateUrl(form, links) {
    if (links.update === null) {
        return;
    }

    updateFormActionAndMethod(form, links.update, 'PUT');
}

/**
 * @param {HTMLFormElement} form
 * @param {string} action
 * @param {string} method
 */
function updateFormActionAndMethod(form, action, method) {
    form.action = action;

    let methodInput = form.querySelector('input[name="_method"]');
    if (methodInput === null) {
        methodInput = document.createElement('input');
        methodInput.setAttribute('type', 'hidden');
        methodInput.setAttribute('name', '_method');

        form.prepend(methodInput);
    }

    /** @type {HTMLInputElement} */
    const input = /** @type {HTMLInputElement} */ (methodInput);
    input.value = method;
}

/**
 * @param {HTMLFormElement} form
 * @param {string|null} instructorExplanationHtml
 */
function showInstructorExplanation(form, instructorExplanationHtml) {
    if (instructorExplanationHtml === null) return;

    const quizResult = form.querySelector('.js-quizResult');
    if (!(quizResult instanceof HTMLElement)) {
        throw new Error('MCQ result container not found.');
    }

    quizResult.insertAdjacentHTML('afterend', instructorExplanationHtml);
}

/**
 * @param {unknown} response
 * @returns {boolean}
 */
function isQuizResponse(response) {
    return typeof response === 'object'
        && response !== null
        && 'quizType' in response
        && ['multipleChoiceQuestion', 'openEndedQuestion'].includes(String(response.quizType));
}

/**
 * Submit Answer (for any Quiz type) using AJAX request.
 * The form should be validated before calling this method.
 *
 * @param {HTMLFormElement} form
 * @see validateAndSubmitOEQAnswer
 */
async function submitValidatedAnswer(form) {
    const formData = new FormData(form);

    const quizId = form.dataset.quizId;
    /** @type {HTMLButtonElement} */
    const submitButton = form.querySelector('button[type="submit"]');

    formData.append('answer', formData.get(quizId));
    formData.delete(quizId); // deduplicate data

    submitButton.disabled = true;
    form.removeAttribute('data-dirty');
    enableWindowUnloadEvent(false);

    /** @type {QuizResponse} */
    const response = await getHttpClient()
        .post(form.action, formData)
        .catch((error) => {
            /** @type {HTMLButtonElement} */
            const button = form.querySelector('button');
            button.disabled = true;

            const errorThrown = error.message;
            const errorMessage =
                error.status === 401
                    ? t('Failed to submit quiz answer: save your answer and re-login to try again.')
                    : t('Failed to submit quiz answer: :error', {error: errorThrown});
            Notification.error(errorMessage, form);
        });

    // Any 2xx that is not the answer JSON (non-2xx responses are handled by the catch above)
    if (response !== undefined && !isQuizResponse(response)) {
        submitButton.disabled = false;
        form.setAttribute('data-dirty', '');
        if (form.classList.contains('js-oeqForm')) {
            unsavedOeqForm = form;
        }
        enableWindowUnloadEvent(true);
        Notification.error(t('Failed to submit quiz answer. Please try again, or copy your answer and refresh the page.'), form);

        /** @type {string} */
        let received = typeof response;
        if (response === null) {
            received = 'an empty body';
        } else if (response instanceof Response) {
            received = response.headers.get('content-type') ?? 'no content type';
        }
        window.dispatchEvent(new CustomEvent('log:error', {
            detail: new Error(`Unexpected quiz answer response (${received}) from ${form.action}`),
        }));
        return;
    }

    if (response) {
        /** @type {HTMLInputElement} */
        const lessonIdInput = document.querySelector('#lessonId');
        /** @type {HTMLInputElement} */
        const lessonItemIdInput = document.querySelector('#lessonItemId');
        const lessonId = Number(lessonIdInput.value);
        const lessonItemId = Number(lessonItemIdInput.value);

        // common for all types of quizzes
        form.querySelectorAll('input[type="radio"]').forEach((radioButton) => {
            /** @type {HTMLInputElement} */
            const input = /** @type {HTMLInputElement} */ (radioButton);
            input.disabled = true;
        });
        form.querySelectorAll('.js-quizSubmit').forEach((submitButton) => {
            submitButton.classList.add('hidden');
        });
        form.querySelector('.js-quizRemarks')?.classList.add('hidden');
        form.querySelector('.js-quiz-after-submit-message')?.classList.remove('hidden');

        /**
         * trigger custom event, that can be listened by any element,
         * panels, like lessonNavigator or rankingPanel
         */
        switch (response.quizType) {
            case 'multipleChoiceQuestion': {
                /** @type {MultipleChoiceQuizAnswer} */
                const mcqResult = /** @type {MultipleChoiceQuizAnswer} */ (response.result);
                afterAnswerSubmitDisplayMcqResult(form, mcqResult.pointsAdded, mcqResult.correctChoice, mcqResult.instructorExplanationHtml);
                break;
            }
            case 'openEndedQuestion': {
                /** @type {OpenEndedQuizAnswer} */
                const oeqResult = /** @type {OpenEndedQuizAnswer} */ (response.result);
                afterAnswerSubmitDisplayOeqResult(form, oeqResult.acceptedText);
                break;
            }
            default:
                throw new Error('Unknown quiz type.');
        }

        // Track daily learning activity
        updateDailyLearningStreak();

        afterAnswerSubmitUpdateUrl(form, response.links);
        determineToAnimateNextLessonButton();

        /** @type {CourseAnswerSubmittedEventDetails} */
        const answerSubmittedEventData = {
            result: {
                pointsAdded: (response.quizType === 'multipleChoiceQuestion' ? /** @type {MultipleChoiceQuizAnswer} */ (response.result).pointsAdded : 0) || 0,
                potentialPointsAdded: (response.quizType === 'openEndedQuestion' ? /** @type {OpenEndedQuizAnswer} */ (response.result).potentialPointsAdded : 0) || 0,
                earnedScore: response.quizType === 'multipleChoiceQuestion' ? /** @type {MultipleChoiceQuizAnswer} */ (response.result).earnedScore : null,
            },
            isLessonItemCompleted: response.isLessonItemCompleted,
            isLessonCompleted: response.isLessonCompleted,
            lessonId: lessonId,
            lessonItemId: lessonItemId,
            quizResultElement: /** @type {HTMLElement|null} */ (form.querySelector('.js-quizResult')),
        };

        document.dispatchEvent(new CustomEvent('courseAnswerSubmitted', {
            detail: answerSubmittedEventData,
        }));
    }

}

/**
 * Validate and submit (is valid) the form with one quiz answer.
 * @param {Event} submitEvent - submit event, where target - form
 */
async function submitMCQAnswer(submitEvent) {
    submitEvent.preventDefault();

    /** @type {HTMLFormElement} */
    const form = /** @type {HTMLFormElement} */ (submitEvent.target);

    submitValidatedAnswer(form);
}

/**
 * Validate and submit (is valid) the form with one quiz answer.
 * @param {Event} submitEvent - submit event, where target - form
 */
async function validateAndSubmitOEQAnswer(submitEvent) {
    submitEvent.preventDefault();

    /** @type {HTMLFormElement} */
    const form = /** @type {HTMLFormElement} */ (submitEvent.target);
    /** @type {HTMLTextAreaElement} */
    const textArea = form.querySelector('textarea');

    const wordsRequiredByConfirmDialog = 10;
    if (countWords(textArea.value) < wordsRequiredByConfirmDialog) {
        // If confirmation required - display a popup window and bring handler to confirmation button
        const result = await requestDialog(form.dataset.confirmationId);
        if (result !== 'CONFIRM_ACTION') {
            // Do not submit form until member confirmed it
            return;
        }
    }

    const spinner = new Spinner(form);
    spinner.start(t('Submitting your answer…'), t('Please wait while we process your response.'));

    await submitValidatedAnswer(form);

    spinner.stop();
}

/** @type {HTMLFormElement|null} */
let unsavedOeqForm = null;

/**
 * Activate/deactivate a window unload event to prevent data loose before leaving the page
 * after some changes have been made by a user.
 *
 * @param {boolean} isEnabled
 */
function enableWindowUnloadEvent(isEnabled) {
    if (isEnabled) {
        // eslint-disable-next-line unicorn/prefer-add-event-listener
        window.onbeforeunload = function () {
            // Scroll to the unsaved OEQ after dialog resolution if navigation is cancelled
            setTimeout(scrollToUnsavedOeq, 0);

            return t('If you leave the page some changes may not be saved.');
        };
    } else {
        const hasOtherDirtyQuizzes = !!document.querySelector('.js-mcqForm[data-dirty], .js-oeqForm[data-dirty]');
        if (!hasOtherDirtyQuizzes) {
            // eslint-disable-next-line unicorn/prefer-add-event-listener
            window.onbeforeunload = null;
            unsavedOeqForm = null;
        }
    }
}

/**
 * Scrolls to the OEQ form with unsaved changes so the user can find it after dismissing the beforeunload dialog.
 */
function scrollToUnsavedOeq() {
    if (document.visibilityState !== 'visible') return;

    if (unsavedOeqForm instanceof HTMLElement) {
        unsavedOeqForm.scrollIntoView({block: 'center', behavior: 'smooth'});
    }
}

/**
 * Activate Form Submit Button on radio checked event
 * @param {Event} radioCheckedEvent
 */
function activateFormSubmitButton(radioCheckedEvent) {
    /** @type {HTMLElement} */
    const target = /** @type {HTMLElement} */ (radioCheckedEvent.target);
    const form = target.closest('form');
    /** @type {HTMLButtonElement} */
    const submitButton = form.querySelector('.js-quizSubmit');

    submitButton.removeAttribute('disabled');
    form.setAttribute('data-dirty', '');
    enableWindowUnloadEvent(true);
}

/**
 * @param {HTMLElement} form
 */
function activateReattemptForm(form) {
    form.querySelector('.js-quizAnswered')?.classList.add('hidden');
    form.querySelector('.js-quizResult')?.classList.add('hidden');
    form.querySelector('.js-quizReattempt')?.classList.add('hidden');
    form.querySelector('.js-quizEdit')?.classList.add('hidden');
    /** @type {HTMLTextAreaElement} */
    const textarea = form.querySelector('textarea[disabled]');
    textarea?.removeAttribute('disabled');
    form.querySelector('rt-editor[disabled]')?.removeAttribute('disabled');
    form.querySelector('.js-quizOeqInputReattempt')?.classList.remove('hidden');
    form.querySelector('rt-editor')?.classList.remove('hidden');
}

/**
 * Handler is triggerent when quiz form clicked
 * @param {HTMLElement} quizForm closest form element
 * @param {HTMLElement} eventTarget element that fired click event
 */
function clickQuizForm(quizForm, eventTarget) {
    const editQuizButton = eventTarget.closest('.js-quizEdit');
    if (editQuizButton) {
        /** @type {HTMLButtonElement} */
        const submitButton = /** @type {HTMLButtonElement} */ (quizForm.querySelector('.js-quizSubmitChanges'));
        submitButton.disabled = true;
        submitButton.classList.remove('hidden');

        activateReattemptForm(quizForm);
    } else {
        const reAttemptButton = eventTarget.closest('.js-quizReattempt');
        if (reAttemptButton) {
            quizForm.querySelector('.js-quizSubmitAnswer').classList.remove('hidden');

            activateReattemptForm(quizForm);
        }
    }
}

/**
 * Handler is triggered when quiz RTE content changed
 * @param {HTMLElement} richTextEditor
 */
function changeRteContent(richTextEditor) {
    /** @type {HTMLTextAreaElement} */
    const textarea = richTextEditor.querySelector('textarea');
    /** @type {HTMLElement} */
    const rte = /** @type {HTMLElement} */ (richTextEditor);
    // Get value from the rich text editor element or textarea
    const rteValue = (/** @type {HTMLElement & {value?: string}} */ (rte).value) || textarea.value || '';
    const canSaveChanges = countWords(rteValue) >= textarea.minLength;

    unsavedOeqForm = canSaveChanges ? textarea.form : null;
    textarea.form.toggleAttribute('data-dirty', canSaveChanges);
    enableWindowUnloadEvent(canSaveChanges);

    textarea.form.querySelectorAll('.js-quizSubmit').forEach((submitButton) => {
        /** @type {HTMLButtonElement} */
        const button = /** @type {HTMLButtonElement} */ (submitButton);
        button.disabled = !canSaveChanges;
    });
}

/** [no description; please add] */
function determineToAnimateNextLessonButton() {
    const mcqQuizForm = [...document.querySelectorAll('.js-mcqForm')];
    const oeqQuizForm = [...document.querySelectorAll('.js-oeqForm')];

    if (mcqQuizForm.length === 0 && oeqQuizForm.length === 0) {
        highlightNextButton();
    } else {
        const gradedMCQQuizAnswer = [...document.querySelectorAll('.js-mcqForm .js-quizResult .message')];
        const gradedOEQQuizAnswer = [...document.querySelectorAll('.js-oeqForm .js-quizResult .message')];
        const awaitingQuizAnswer = [...document.querySelectorAll('.js-oeqForm .js-quizAwaitGradingMessage:not(.hidden)')];
        if (gradedMCQQuizAnswer.length + gradedOEQQuizAnswer.length + awaitingQuizAnswer.length === mcqQuizForm.length) {
            highlightNextButton();
        }
    }
}

/**
 * Promote the "Next Lesson" button to its answered-state CTA: a pulsing primary (blue) button.
 * Before the quizzes are answered it stays a quiet ghost button (no pulse).
 */
function highlightNextButton() {
    const buttonNextLesson = document.querySelector('.js-buttonNextLesson');
    if (buttonNextLesson instanceof HTMLElement) {
        buttonNextLesson.classList.remove('button--ghost');
        buttonNextLesson.classList.add('button--primary');
        Button.addPulse(buttonNextLesson);
    }
}

/**
 * Handle browser back-forward navigation cache.
 * When user navigates back after submitting a quiz, reload to get fresh server state.
 * This handles both bfcache (event.persisted) and regular browser cache scenarios.
 */
function handleBackForwardCache() {
    // Handle bfcache restoration
    window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
            window.location.reload();
        }
    });

    // Handle regular cache scenario using Navigation Timing API
    // This detects back/forward navigation even when bfcache is not used
    const navigationEntries = window.performance.getEntriesByType('navigation');
    if (navigationEntries.length > 0) {
        const navigationEntry = /** @type {PerformanceNavigationTiming} */ (navigationEntries[0]);
        if (navigationEntry.type === 'back_forward') {
            window.location.reload();
        }
    }
}

/**
 * Mock daily streak for testing purposes
 * Call from browser console: window.mockDailyStreak(5)
 * @param {number} days - Number of days for the streak
 */
// @ts-ignore - Adding to window for debugging purposes
window.mockDailyStreak = function (days) {
    if (typeof days !== 'number' || days < 1) {

        console.warn('mockDailyStreak: Please provide a positive number of days');
        return;
    }
    showDailyStreakBadge(days);
};

/**
 * Init page, register event handlers
 */
function initPage() {
    handleBackForwardCache();
    preloadSounds();

    document.querySelectorAll('.js-mcqForm').forEach((quizForm) => {
        /** @type {HTMLFormElement} */
        const form = /** @type {HTMLFormElement} */ (quizForm);
        form.addEventListener('submit', submitMCQAnswer);
        form.querySelectorAll('.js-quizMultiChoiceRadioInput').forEach((radioInput) => {
            radioInput.addEventListener('change', activateFormSubmitButton);
        });
    });
    document.querySelectorAll('.js-oeqForm').forEach((quizForm) => {
        /** @type {HTMLFormElement} */
        const form = /** @type {HTMLFormElement} */ (quizForm);
        form.addEventListener('submit', validateAndSubmitOEQAnswer);

        form.addEventListener('click', (event) => {
            /** @type {HTMLElement} */
            const target = /** @type {HTMLElement} */ (event.target);
            clickQuizForm(/** @type {HTMLElement} */ (form), target);
        });
        form.addEventListener('rte:change', (event) => {
            if (event.target instanceof HTMLElement) {
                changeRteContent(event.target);
            }
        });
    });

    determineToAnimateNextLessonButton();
}

// init page when the document is ready
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', initPage) : initPage();
